import React, { createContext, useState, useEffect } from "react"
import useFetch from "../hooks/use-fetch"
import { AxiosError } from "axios"
import validator from "validator"
import { useCookies } from "react-cookie";

import User from "../components/models/User";

export type errorResponseType = {
    message: string
    detail?: string
} | null

type PropsType = {
    children: React.ReactNode
}

export const tokenKey = "token"
export const refreshToken = "refresh"

type ContextType = {
    isAuth: boolean
    user?: User
    onLogin: (email: string, password: string) => Promise<errorResponseType>
    onRegister: (
        email: string,
        password: string,
        firstName: string,
        lastName: string,
    ) => Promise<errorResponseType>
    onLogout: () => void
}

const initialErrorResponse: errorResponseType = {
    message: "Something went wrong..."
}

const initialContext: ContextType = {
    isAuth: false,
    user: undefined,
    onLogin: () => Promise.resolve(initialErrorResponse),
    onRegister: () => Promise.resolve(initialErrorResponse),
    onLogout: () => {},
}

const validatePsw = (value: string) => {
    if (!validator.isStrongPassword(value, {
        minLength: 8,
        minLowercase: 1,
        minUppercase: 1,
        minNumbers: 1,
        minSymbols: 1
    })) {
        const err: errorResponseType = {
            message: "Password must:\n\t - be at least 8 characters long;\n\t - have at least an uppercase character;\n\t - have at least a lowercase character;\n\t - have at least a number;\n\t - have at least a special character."
        }
        throw err
    }
}

export const AuthContext = createContext<ContextType>(initialContext)

const AuthProvider = (props: PropsType) => {
    const { get, post } = useFetch()
    const [isAuth, setIsAuth] = useState(!!localStorage.getItem(tokenKey))
    const [user, setUser] = useState<User | undefined>(undefined)
    const [cookies, setCookie, removeCookie] = useCookies(["access_token"]);

    useEffect(() => {

        const checkAuth = async () => {
            const token = localStorage.getItem(tokenKey)
            if (!token) {
                setIsAuth(false)
                return
            }

            try {
                const data = await get('/auth/me/')
                setIsAuth(true)
                setUser(data)
                localStorage.setItem('user', JSON.stringify(data))
            } catch(error){
                setIsAuth(false)
                setUser(undefined)
                localStorage.removeItem(tokenKey)
                localStorage.removeItem('user')
            }

        }

        checkAuth()
    }, [])

    const onLogin = async (email: string, password: string) => {
        try {
            const data = await post('/auth/login/', { email, password })
            localStorage.setItem(tokenKey, data.access_token)
            localStorage.setItem(refreshToken, data.refresh_token)
            setIsAuth(true)
            setCookie("access_token", data.access_token, {
                path: "/",
                secure: window.location.protocol === "https:",
                sameSite: "strict",
                maxAge: 60 * 60 * 24 * 7, // 7 days
              });

            const userData: User = data.user
            setUser(userData)
            localStorage.setItem('user', JSON.stringify(userData))
            return null
        } catch (error) {
            const err = error as AxiosError
            return err.response?.data as errorResponseType ?? initialErrorResponse
        }
    }

    const onRegister = async (
        email: string,
        password: string,
        firstName: string,
        lastName: string,
    ) => {
        try {
            validatePsw(password)

            const payload = {
                email,
                password,
                firstName,
                lastName,
            }
            await post('/auth/register/', payload)
            return null
        } catch (error) {
            const err = error as AxiosError
            if (err?.message){
                return {message: err.message}
            }
            return err.response?.data as errorResponseType ?? { message: 'Registration failed.' }
        }
    }

    const onLogout = async () => {
        await post('/auth/logout/', {})
        localStorage.removeItem(tokenKey)
        localStorage.removeItem('user')
        setIsAuth(false)
        setUser(undefined)
    }

    const value: ContextType = {
        isAuth,
        user,
        onLogin,
        onLogout,
        onRegister
    }

    return (
        <AuthContext.Provider value={value}>
            {props.children}
        </AuthContext.Provider>
    )
}

export default AuthProvider
